import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getActiveProfileId } from '../lib/utils';
import { X, Play, Plus, ThumbsUp, ThumbsDown, Volume2, VolumeX, Share2, Eye, EyeOff, Ear, EarOff, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ModalTooltip({ text, visible }) {
  if (!visible || !text) return null;
  return (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 pointer-events-none z-50">
      <div className="bg-white text-black text-xs font-bold px-3 py-1.5 rounded whitespace-nowrap shadow-lg">{text}</div>
      <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white mx-auto" />
    </div>
  );
}

function ModalRatingPopup({ rating, onRate }) {
  const [hovered, setHovered] = useState(null);
  const options = [
    { id: 'dislike', icon: ThumbsDown, label: 'Pas pour moi', size: 18 },
    { id: 'like', icon: ThumbsUp, label: 'J\'aime bien ce titre', size: 18 },
    { id: 'love', icon: ThumbsUp, label: 'J\'adore !', size: 16, double: true },
  ];
  return (
    <div className="flex items-center">
      {hovered && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none z-50">
          <div className="bg-white text-black text-xs font-bold px-3 py-1.5 rounded whitespace-nowrap shadow-lg">{options.find(o => o.id === hovered)?.label}</div>
          <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white mx-auto" />
        </div>
      )}
      <div className="flex items-center gap-0.5 bg-[#2a2a2a] rounded-full px-1.5 py-1 shadow-xl border border-white/10">
        {options.map(opt => {
          const Icon = opt.icon;
          const isActive = rating === opt.id;
          return (
            <button key={opt.id} onMouseEnter={() => setHovered(opt.id)} onMouseLeave={() => setHovered(null)}
              onClick={(e) => { e.stopPropagation(); onRate(isActive ? null : opt.id); }}
              className={`flex items-center justify-center rounded-full transition-all duration-200 w-10 h-10 ${isActive ? 'text-white scale-110' : 'text-white/70 hover:text-white hover:scale-110'} ${hovered === opt.id ? 'bg-white/15' : ''}`}
              style={isActive ? { animation: 'rate-pop 0.35s ease-out' } : undefined}>
              {opt.double ? (
                <span className="flex items-center -space-x-1.5">
                  <Icon size={opt.size} fill={isActive ? '#E9641A' : 'none'} className={isActive ? 'text-[#E9641A]' : ''} />
                  <Icon size={opt.size} fill={isActive ? '#E9641A' : 'none'} className={isActive ? 'text-[#E9641A]' : ''} />
                </span>
              ) : (
                <Icon size={opt.size} fill={isActive ? '#E9641A' : 'none'} className={isActive ? 'text-[#E9641A]' : ''} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ContentModal({ contentId, onClose, sourceRect }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const [phase, setPhase] = useState('entering');
  const [watchProgress, setWatchProgress] = useState(null);
  const [adminFilters, setAdminFilters] = useState([]);
  const [inList, setInList] = useState(false);
  const [listAnim, setListAnim] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(null);
  const [ratingAnim, setRatingAnim] = useState(false);
  const [recRow, setRecRow] = useState(null);
  const [epSeen, setEpSeen] = useState({ watched: [], currentEp: null, progress: 0 });
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const { activeProfile } = useAuth();
  const profileId = activeProfile?.id || '';

  // Warm browser + CDN caches (manifest, variant playlist, first segment) for instant playback
  const prefetchStream = useCallback(async (c) => {
    try {
      const src = c?.hls_url;
      if (!src || c?.transcode_status !== 'COMPLETE') return;
      const res = await fetch(src, { mode: 'cors' });
      if (!res.ok) return;
      const master = await res.text();
      const base = src.substring(0, src.lastIndexOf('/') + 1);
      const variants = master.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#'));
      if (variants.length === 0) return;
      const v = variants[Math.min(1, variants.length - 1)];
      const vUrl = v.startsWith('http') ? v : base + v;
      const vRes = await fetch(vUrl, { mode: 'cors' });
      if (!vRes.ok) return;
      const seg = (await vRes.text()).split('\n').map(l => l.trim()).find(l => l && !l.startsWith('#'));
      if (!seg) return;
      const segUrl = seg.startsWith('http') ? seg : vUrl.substring(0, vUrl.lastIndexOf('/') + 1) + seg;
      fetch(segUrl, { mode: 'cors' }).catch(() => {});
    } catch {}
  }, []);

  useEffect(() => {
    if (!contentId) return;
    import('./ActivityTracker').then(m => m.trackEvent('content_view', 'Fiche (modal)', contentId)).catch(() => {});
    const fetchContent = async () => {
      try {
        const [contentRes, progressRes, filtersRes] = await Promise.all([
          axios.get(`${API}/contents/${contentId}`, { withCredentials: true }),
          axios.get(`${API}/watch-progress/${contentId}`, { params: { profile_id: profileId }, withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/filters`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setContent(contentRes.data);
        prefetchStream(contentRes.data);
        setAdminFilters(filtersRes.data || []);
        if (progressRes.data && progressRes.data.progress > 5 && progressRes.data.progress < 95) {
          setWatchProgress(progressRes.data);
        }
        if (progressRes.data) {
          setEpSeen({
            watched: progressRes.data.watched_episode_ids || [],
            currentEp: progressRes.data.episode_id || null,
            progress: progressRes.data.progress || 0,
          });
        }
        // "Parce que vous avez regardé X" (history-based, excludes the open content)
        axios.get(`${API}/recommendations`, { params: { profile_id: profileId }, withCredentials: true })
          .then(r => {
            const row = (Array.isArray(r.data) ? r.data : []).map(x => ({
              ...x, items: (x.items || []).filter(i => i.id !== contentId),
            })).find(x => x.based_on_id !== contentId && x.items.length >= 3);
            setRecRow(row || null);
          })
          .catch(() => {});
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
    document.body.style.overflow = 'hidden';
    // Trigger open animation after mount
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase('visible'));
    });
    return () => { document.body.style.overflow = ''; };
  }, [contentId]);

  const stopTrailer = useCallback(() => {
    const v = videoRef.current;
    if (v) { v.pause(); v.muted = true; v.removeAttribute('src'); v.load(); }
  }, []);

  const handleClose = useCallback(() => {
    if (phase === 'leaving') return;
    stopTrailer();
    setPhase('leaving');
    setTimeout(() => {
      setPhase('gone');
      onClose();
    }, 600);
  }, [onClose, phase, stopTrailer]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const toggleList = async () => {
    try {
      if (inList) {
        await axios.delete(`${API}/watchlist/${contentId}`, { params: { profile_id: getActiveProfileId() }, withCredentials: true });
        setInList(false);
      } else {
        await axios.post(`${API}/watchlist/${contentId}`, { profile_id: getActiveProfileId() }, { withCredentials: true });
        setInList(true);
        setListAnim(true);
        setTimeout(() => setListAnim(false), 400);
      }
    } catch {}
  };

  const handleRate = (r) => {
    setRating(r);
    if (r) { setRatingAnim(true); setTimeout(() => setRatingAnim(false), 400); }
    setShowRating(false);
  };

  // Check watchlist status
  useEffect(() => {
    if (!contentId) return;
    axios.get(`${API}/watchlist`, { params: { profile_id: getActiveProfileId() }, withCredentials: true })
      .then(r => { if (r.data?.some(c => c.id === contentId)) setInList(true); })
      .catch(() => {});
  }, [contentId]);

  const modalRef = useRef(null);

  if (!contentId) return null;

  const isVisible = phase === 'visible';
  const isLeaving = phase === 'leaving';

  // Compute transform-origin from sourceRect
  const originStyle = {};
  if (sourceRect) {
    const cx = sourceRect.left + sourceRect.width / 2;
    const cy = sourceRect.top + sourceRect.height / 2;
    originStyle.transformOrigin = `${cx}px ${cy}px`;
  }

  const subtitleLangDisplay = content
    ? (content.burned_subtitles_lang
      || ((content.language && content.language !== 'Français' && (content.subtitles || []).some(s => (s.language || '').toLowerCase().startsWith('fran'))) ? 'Français' : ''))
    : '';

  return (
    <div
      className={`fixed inset-0 z-[100] flex justify-center transition-all duration-[1000ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? 'bg-black/70' : 'bg-black/0'}`}
      onClick={handleClose}
      data-testid="content-modal-overlay"
      style={sourceRect ? originStyle : undefined}
    >
      <div
        ref={modalRef}
        className={`relative w-full max-w-[900px] mt-8 mb-8 mx-4 overflow-y-auto rounded-xl hide-scrollbar`}
        style={{
          background: '#181018',
          maxHeight: 'calc(100vh - 64px)',
          transition: `opacity 1000ms cubic-bezier(0.16,1,0.3,1), transform 1000ms cubic-bezier(0.16,1,0.3,1)`,
          opacity: isVisible ? 1 : isLeaving ? 0 : 0,
          transform: isVisible
            ? 'scale(1) translateY(0)'
            : isLeaving
              ? 'scale(0.92) translateY(10px)'
              : sourceRect
                ? `scale(0.3) translateY(${sourceRect.top - window.innerHeight / 2 + sourceRect.height / 2}px)`
                : 'scale(0.5) translateY(40px)',
          transformOrigin: sourceRect
            ? `${sourceRect.left + sourceRect.width / 2 - (window.innerWidth / 2 - 450)}px ${sourceRect.top}px`
            : 'center top',
        }}
        onClick={e => e.stopPropagation()}
        data-testid="content-modal"
      >
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="w-10 h-10 border-[3px] border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" />
          </div>
        ) : content ? (
          <>
            {/* Close button */}
            <button
              data-testid="modal-close-btn"
              onClick={handleClose}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-[#181018]/80 flex items-center justify-center text-white hover:bg-[#181018] transition-colors"
            >
              <X size={22} />
            </button>

            {/* Video/Image header */}
            <div className="relative aspect-video w-full">
              {content.type !== 'game' && content.trailer_url ? (
                <video ref={videoRef} src={content.trailer_url} className="w-full h-full object-cover" muted={muted} autoPlay loop playsInline
                  onLoadedMetadata={(e) => { const ts = parseFloat(content.trailer_trim_start) || 0; if (ts > 0) e.target.currentTime = ts; }}
                  onTimeUpdate={(e) => { const ts = parseFloat(content.trailer_trim_start) || 0; const te = parseFloat(content.trailer_trim_end) || 0; if (te > 0 && e.target.currentTime >= te) e.target.currentTime = ts; }}
                  data-testid="modal-trailer-video" />
              ) : (
                <>
                  <img src={content.mobile_banner_url || content.poster_url || content.banner_url || content.thumbnail_url} alt={content.title} className="w-full h-full object-cover sm:hidden" data-testid="modal-fallback-img-mobile" />
                  <img src={content.banner_url || content.thumbnail_url || content.poster_url} alt={content.title} className="w-full h-full object-cover hidden sm:block" data-testid="modal-fallback-img" />
                </>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#181018] via-transparent to-transparent" />

              {/* Title & buttons */}
              <div className="absolute bottom-0 left-0 right-0 px-8 md:px-12 pb-8">
                {content.logo_url ? (
                  <img src={content.logo_url} alt={content.title} className="max-h-20 md:max-h-28 w-auto mb-5 drop-shadow-xl" data-testid="modal-logo" />
                ) : (
                  <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-5 tracking-tight" data-testid="modal-title">
                    {content.title}
                  </h1>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  <style>{`
                    @keyframes rate-pop { 0%{transform:scale(1)} 30%{transform:scale(1.35)} 60%{transform:scale(0.9)} 100%{transform:scale(1.1)} }
                    @keyframes list-check { 0%{transform:scale(0.5) rotate(-20deg);opacity:0} 50%{transform:scale(1.2) rotate(5deg);opacity:1} 100%{transform:scale(1) rotate(0deg);opacity:1} }
                  `}</style>
                  {content.type === 'game' ? (
                    <button
                      data-testid="modal-play-btn"
                      onClick={() => { stopTrailer(); handleClose(); setTimeout(() => navigate(`/game/${content.id}`), 450); }}
                      className="flex items-center gap-2 bg-[#E9641A] text-white font-bold px-8 py-3 rounded-md hover:bg-[#FE975E] transition-all text-base"
                    >
                      <Play size={22} fill="white" />
                      Jouer
                    </button>
                  ) : (
                    <button
                      data-testid="modal-play-btn"
                      onClick={() => {
                        document.querySelectorAll('video').forEach(v => { v.pause(); v.muted = true; });
                        stopTrailer(); handleClose();
                        let target = '';
                        if (content.type === 'series' && (content.episodes || []).length) {
                          const eps = [...content.episodes].sort((a, b) => (a.season || 1) - (b.season || 1) || (a.episode || 0) - (b.episode || 0));
                          let ep = null;
                          if (epSeen.currentEp && epSeen.progress > 0 && epSeen.progress < 95) ep = eps.find(e => e.id === epSeen.currentEp);
                          if (!ep) ep = eps.find(e => !epSeen.watched.includes(e.id)) || eps[0];
                          if (ep) target = `?episode=${ep.id}`;
                        }
                        setTimeout(() => navigate(`/watch/${content.id}${target}`), 450);
                      }}
                      className={`flex items-center gap-2 ${watchProgress ? 'bg-[#E9641A] hover:bg-[#FE975E]' : 'bg-white hover:bg-white/80 text-black'} font-bold px-8 py-3 rounded-md transition-all text-base`}
                    >
                      <Play size={22} fill={watchProgress ? 'white' : 'black'} className={watchProgress ? 'text-white' : 'text-black'} />
                      {watchProgress ? (() => {
                        const t = watchProgress.current_time;
                        if (!t || t <= 0) return 'Reprendre';
                        const h = Math.floor(t / 3600);
                        const m = Math.floor((t % 3600) / 60);
                        const s = Math.floor(t % 60);
                        const timeStr = h > 0 ? `${h}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}` : `${m}:${s.toString().padStart(2,'0')}`;
                        return `Reprendre à ${timeStr}`;
                      })() : (() => {
                        if (content.type === 'series' && (content.episodes || []).length && epSeen.watched.length > 0) {
                          const eps = [...content.episodes].sort((a, b) => (a.season || 1) - (b.season || 1) || (a.episode || 0) - (b.episode || 0));
                          const next = eps.find(e => !epSeen.watched.includes(e.id));
                          if (next) return `Lire S${next.season || 1} E${next.episode || eps.indexOf(next) + 1}`;
                        }
                        return 'Lecture';
                      })()}
                    </button>
                  )}

                  {/* Add/Remove list */}
                  <div className="relative" onMouseEnter={() => setHoveredBtn('list')} onMouseLeave={() => setHoveredBtn(null)}>
                    <ModalTooltip text={inList ? 'Supprimer de Ma liste' : 'Ajouter à Ma liste'} visible={hoveredBtn === 'list' && !showRating} />
                    <button data-testid="modal-add-btn" onClick={toggleList}
                      className={`w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all duration-200 hover:scale-110 ${
                        inList ? 'border-white/60 text-white' : 'border-white/40 text-white/70 hover:border-white hover:text-white'
                      }`}>
                      {inList ? (
                        <Check size={20} strokeWidth={3} style={listAnim ? { animation: 'list-check 0.4s ease-out' } : undefined} />
                      ) : (
                        <Plus size={22} />
                      )}
                    </button>
                  </div>

                  {/* Rating popup inline */}
                  <div className="relative" onMouseEnter={() => { setHoveredBtn('like'); setShowRating(true); }} onMouseLeave={() => { setHoveredBtn(null); setShowRating(false); }}>
                    {showRating ? (
                      <ModalRatingPopup rating={rating} onRate={handleRate} />
                    ) : (
                      <>
                        <ModalTooltip text={rating === 'like' ? 'Noté : j\'aime' : rating === 'love' ? 'Noté : j\'adore' : rating === 'dislike' ? 'Noté : pas pour moi' : 'J\'aime bien ce titre'} visible={hoveredBtn === 'like'} />
                        <button data-testid="modal-like-btn"
                          className={`w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all duration-200 hover:scale-110 ${
                            rating ? 'border-white/60 text-[#E9641A]' : 'border-white/40 text-white/70 hover:border-white hover:text-white'
                          }`}
                          style={ratingAnim ? { animation: 'rate-pop 0.35s ease-out' } : undefined}>
                          {rating === 'dislike' ? (
                            <ThumbsDown size={19} fill="#E9641A" />
                          ) : rating === 'love' ? (
                            <span className="flex items-center -space-x-1.5">
                              <ThumbsUp size={16} fill="#E9641A" className="text-[#E9641A]" />
                              <ThumbsUp size={16} fill="#E9641A" className="text-[#E9641A]" />
                            </span>
                          ) : rating === 'like' ? (
                            <ThumbsUp size={19} fill="#E9641A" />
                          ) : (
                            <ThumbsUp size={18} />
                          )}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Share */}
                  <div className="relative" onMouseEnter={() => setHoveredBtn('share')} onMouseLeave={() => setHoveredBtn(null)}>
                    <ModalTooltip text="Partager" visible={hoveredBtn === 'share'} />
                    <button className="w-11 h-11 rounded-full border-2 border-white/40 flex items-center justify-center hover:border-white text-white/70 hover:text-white transition-all duration-200 hover:scale-110">
                      <Share2 size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Mute button */}
              {content.trailer_url && content.type !== 'game' && (
                <button data-testid="modal-mute-btn" onClick={() => setMuted(!muted)}
                  className="absolute bottom-8 right-8 md:right-12 z-10 w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white/60 hover:text-white bg-black/30 backdrop-blur-sm transition-all">
                  {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
              )}

              {/* Watch progress bar */}
              {watchProgress && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15 z-10" data-testid="modal-progress-bar">
                  <div className="h-full bg-[#E9641A]" style={{ width: `${watchProgress.progress}%` }} />
                </div>
              )}
            </div>

            {/* Info section */}
            <div className="px-8 md:px-12 py-8">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="text-base font-semibold text-white/80">{content.year}</span>
                    <span className="text-base text-white/50">{content.duration}</span>
                    <span className="px-2 py-0.5 border border-white/25 rounded text-xs font-semibold text-white/70">{content.rating || 'HD'}</span>
                    {content.language && <span className="px-2 py-0.5 border border-white/25 rounded text-xs font-semibold text-white/70">{content.language}</span>}
                    {subtitleLangDisplay && <span className="px-2 py-0.5 border border-white/25 rounded text-xs font-semibold text-white/70" data-testid="modal-subtitles-badge">Sous-titres {subtitleLangDisplay.toLowerCase()}</span>}
                    {content.age_rating && <span className="px-2 py-0.5 border border-white/25 rounded text-xs font-semibold text-white/70">{content.age_rating}</span>}
                    {content.country && <span className="px-2 py-0.5 border border-white/25 rounded text-xs font-semibold text-white/70">{content.country}</span>}
                  </div>
                  {content.content_filters?.length > 0 && (
                    <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="modal-filters-badge">
                      <span className="flex items-center gap-1.5 px-3 py-1 bg-[#E9641A]/15 border border-[#E9641A]/40 rounded-lg text-xs font-semibold text-[#E9641A]">
                        {content.type === 'series' ? 'Cette série contient des filtres' : 'Ce film contient des filtres'}
                      </span>
                      {(() => {
                        const uniqueIds = [...new Set(content.content_filters.map(f => f.filter_id || f.type))];
                        return uniqueIds.map(fId => {
                          const def = adminFilters.find(af => af.id === fId);
                          if (!def) return null;
                          const isMute = def.action === 'mute';
                          return (
                            <span key={fId} className="flex items-center gap-1 px-2 py-1 bg-white/5 border border-white/10 rounded-lg text-[11px] text-white/60" title={def.name}>
                              {isMute ? <Ear size={12} /> : <Eye size={12} />}
                              {def.name}
                            </span>
                          );
                        });
                      })()}
                    </div>
                  )}
                  <p className="text-sm md:text-base text-white/75 leading-relaxed mb-4" data-testid="modal-description">
                    {content.description}
                  </p>
                </div>
                <div className="w-full md:w-64 space-y-3 text-sm flex-none">
                  {content.director && (
                    <p className="text-white/40">
                      <span className="text-white/60">Réalisateur : </span>
                      <span className="text-white/80">{content.director}</span>
                    </p>
                  )}
                  {content.actors?.filter(a => a).length > 0 && (
                    <p className="text-white/40">
                      <span className="text-white/60">Acteurs : </span>
                      <span className="text-white/80">{content.actors.filter(a => a).join(', ')}</span>
                    </p>
                  )}
                  {content.tags?.length > 0 && (
                    <p className="text-white/40">
                      <span className="text-white/60">Genres : </span>
                      <span className="text-white/80">{content.tags.join(', ')}</span>
                    </p>
                  )}
                  <p className="text-white/40">
                    <span className="text-white/60">Type : </span>
                    <span className="text-white/80 capitalize">{content.type === 'series' ? 'Série' : content.type === 'game' ? 'Jeu' : 'Film'}</span>
                  </p>
                  <p className="text-white/40">
                    <span className="text-white/60">Catégorie : </span>
                    <span className="text-white/80 capitalize">{(content.categories || [content.category]).join(', ')}</span>
                  </p>
                  {content.country && (
                    <p className="text-white/40">
                      <span className="text-white/60">Pays : </span>
                      <span className="text-white/80">{content.country}</span>
                    </p>
                  )}
                  {content.language && (
                    <p className="text-white/40">
                      <span className="text-white/60">Langue : </span>
                      <span className="text-white/80">{content.language}{subtitleLangDisplay ? ` · Sous-titres ${subtitleLangDisplay.toLowerCase()}` : ''}</span>
                    </p>
                  )}
                  {content.age_rating && (
                    <p className="text-white/40">
                      <span className="text-white/60">Classification : </span>
                      <span className="text-white/80">{content.age_rating}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Episodes for series */}
            {content.type === 'series' && content.episodes?.length > 0 && (
              <div className="px-8 md:px-12 pb-8">
                <h2 className="text-xl font-bold text-white mb-4">Épisodes</h2>
                <div className="space-y-2">
                  {content.episodes.map((ep, idx) => {
                    const isCurrent = ep.id === epSeen.currentEp;
                    const inProgress = isCurrent && epSeen.progress > 0 && epSeen.progress < 95;
                    const isSeen = epSeen.watched.includes(ep.id) && !inProgress;
                    return (
                    <div key={ep.id} className="flex items-center gap-4 p-4 rounded-lg hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => { handleClose(); setTimeout(() => navigate(`/watch/${content.id}?episode=${ep.id}`), 450); }}>
                      <span className="text-2xl font-light text-white/25 w-8 text-center">{idx + 1}</span>
                      <div className="relative w-28 h-16 rounded overflow-hidden bg-white/5 flex-none">
                        <img src={ep.thumbnail_url || content.banner_url || content.thumbnail_url} alt="" className={`w-full h-full object-cover ${isSeen ? 'opacity-60' : ''}`} />
                        {isSeen && (
                          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow" data-testid={`ep-seen-${ep.id}`}>
                            <Check size={12} className="text-white" strokeWidth={3} />
                          </div>
                        )}
                        {inProgress && (
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/25" data-testid={`ep-progress-${ep.id}`}>
                            <div className="h-full bg-[#E9641A]" style={{ width: `${epSeen.progress}%` }} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white flex items-center gap-2">
                          {ep.title || `Épisode ${ep.episode}`}
                          {isSeen && <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-400">Vu</span>}
                          {inProgress && <span className="text-[10px] font-bold uppercase tracking-wide text-[#FE975E]">En cours</span>}
                        </p>
                        <p className="text-xs text-white/40 mt-0.5">{ep.duration || ''}</p>
                        {ep.description && <p className="text-xs text-white/50 mt-1 line-clamp-2">{ep.description}</p>}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Parce que vous avez regardé X */}
            {recRow && (
              <div className="px-8 md:px-12 pb-8" data-testid="modal-rec-row">
                <h2 className="text-xl font-bold text-white mb-5">Parce que vous avez regardé {recRow.based_on_title}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {recRow.items.slice(0, 6).map(rec => (
                    <div key={rec.id} data-testid={`modal-rec-${rec.id}`}
                      className="bg-[#221828] rounded-lg overflow-hidden cursor-pointer hover:ring-1 hover:ring-white/20 transition-all"
                      onClick={() => window.dispatchEvent(new CustomEvent('openContentModal', { detail: rec.id }))}>
                      <div className="relative aspect-video">
                        <img src={rec.banner_url || rec.thumbnail_url} alt={rec.title} className="w-full h-full object-cover" />
                        {rec.duration && <div className="absolute top-2 right-2 text-xs text-white/80 bg-black/60 px-2 py-0.5 rounded">{rec.duration}</div>}
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-semibold text-white mb-1 line-clamp-1">{rec.title}</p>
                        <p className="text-xs text-white/55 line-clamp-2 leading-relaxed">{rec.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Titres similaires */}
            {content.recommendations?.length > 0 && (
              <div className="px-8 md:px-12 pb-8">
                <h2 className="text-xl font-bold text-white mb-5">Titres similaires</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {content.recommendations.map(rec => (
                    <div key={rec.id} data-testid={`similar-${rec.id}`}
                      className="bg-[#221828] rounded-lg overflow-hidden cursor-pointer hover:ring-1 hover:ring-white/20 transition-all"
                      onClick={() => window.dispatchEvent(new CustomEvent('openContentModal', { detail: rec.id }))}>
                      <div className="relative aspect-video">
                        <img src={rec.banner_url || rec.thumbnail_url} alt={rec.title} className="w-full h-full object-cover" />
                        <div className="absolute top-2 right-2 text-xs text-white/80 bg-black/60 px-2 py-0.5 rounded">{rec.duration}</div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="px-1.5 py-0.5 border border-white/20 rounded text-white/60">16+</span>
                            <span className="px-1.5 py-0.5 border border-white/20 rounded text-white/60">HD</span>
                            <span className="text-white/50">{rec.year}</span>
                          </div>
                          <button onClick={e => e.stopPropagation()}
                            className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center text-white/40 hover:text-white hover:border-white/50 transition-colors">
                            <Plus size={14} />
                          </button>
                        </div>
                        <p className="text-xs text-white/55 line-clamp-3 leading-relaxed">{rec.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* À propos de... */}
            <div className="px-8 md:px-12 pb-12 border-t border-white/5 pt-8">
              <h2 className="text-2xl font-bold text-white mb-6">À propos de {content.title}</h2>
              <div className="space-y-4 text-sm">
                {content.tags?.length > 0 && <p><span className="text-white/40">Genres : </span><span className="text-white/80">{content.tags.join(', ')}</span></p>}
                <p><span className="text-white/40">Type : </span><span className="text-white/80 capitalize">{content.type === 'series' ? 'Série' : 'Film'}</span></p>
                <p><span className="text-white/40">Catégorie : </span><span className="text-white/80 capitalize">{content.category}</span></p>
                <p><span className="text-white/40">Année : </span><span className="text-white/80">{content.year}</span></p>
                {content.duration && <p><span className="text-white/40">Durée : </span><span className="text-white/80">{content.duration}</span></p>}
                <p><span className="text-white/40">Qualité : </span><span className="text-white/80">{content.rating}</span></p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center py-32">
            <p className="text-white/50">Contenu introuvable</p>
          </div>
        )}
      </div>
    </div>
  );
}
